import { AutogenLayoutOfficePayrollProcessing } from "./autogen/AutogenLayoutOfficePayrollProcessing";
import {
    Alignment, Api, Currency, CurrencySettings, DateUtil, LogManager, Model, ModelRow, ModelSearchResult, Navigation,
    ObjectUtil, UrlUtil
} from "@mcleod/core";
import {
    Button, ClickEvent, DataSourceAction, DataSourceExecutionEvent, DataSourceMode, DataSourceRemoteDataChangeEvent,
    Label, Layout, ModelDataChangeType, Panel, SlideoutDecorator, Snackbar, TableRow
} from "@mcleod/components";
import { TimecardProcessing } from "@mcleod/settlement/src/TimecardProcessing";
import { PayablesLookup } from "@mcleod/settlement/src/OrderPayables";
import { getPayrollControlValue, LMEUserSettings } from "@mcleod/general";
import { ArchiveVoidedPayables } from "@mcleod/settlement/src/ArchiveVoidedPayables";
import { RowPayrollControlFields } from "@mcleod/general/src/models/autogen/AutogenModelPayrollControl";
import { OfficePayrollProcessingFilter } from "./OfficePayrollProcessingFilter";
import {
    ModelOfficePayrollProfile, RowOfficePayrollProfile
} from "@mcleod/settlement/officepayroll/src/models/ModelOfficePayrollProfile";
import { OfficePayeeData, OfficePayeeDataLookup, PayeeDataLookup } from "@mcleod/settlement/src/PayeeData";
import { OfficePayeeSelectedProps } from "./OfficePayrollProcessingGroupBy";
import {
    ManageRecurringPayAdjustmentProps, ManageRecurringPayAdjustments
} from "@mcleod/settlement/src/ManageRecurringPayAdjustments";
import { CommonDialogs } from "@mcleod/common";
import { OrderPayablesProcessStepper } from "@mcleod/settlement/src/OrderPayablesProcessStepper";

const log = LogManager.getLogger("lme.backoffice.officepayroll.OfficePayrollProcessing");

export class OfficePayrollProcessing extends AutogenLayoutOfficePayrollProcessing {
    private _profileRow: RowOfficePayrollProfile;
    private profileFilter: OfficePayrollProcessingFilter;
    private payablesLookupMap: PayablesLookup;
    private payeeDataLookup: OfficePayeeDataLookup;
    private timerId: number | null = null;
    private currentSelectedPayee: OfficePayeeSelectedProps | null = null;
    private countdownTimerId: number | null = null;
    private remainingSeconds: number = 30;
    public snackbars: Set<Panel> = new Set();
    private checkingBonusTaxRates: boolean = false;
    private payrollCutoffDate: Date | null = null;
    private recurringDate: Date | null = null;
    public payrollControl: ModelRow;
    public updateCutoffDate: () => Promise<ModelRow | null>;

    onLoad(): void | Promise<void> {
        this.splitterOfficePayrollProcessing.style.overflow = "auto";
        this.layoutPayeeTotals.mainDataSource.url = "lme/backoffice/officepayroll/payee-totals";
        this.layoutPayeeTotals.panelSettlement.visible = false;
        this.sourceVoidedPayablesOfficePayroll.search();
        this.sourceStateFederalTaxDriverSettlement.search();
        this.setupPayeeTotalsLayout();
    }

    async loadProfile(profile?: string | ModelRow) {
        if (profile instanceof ModelRow) {
            await this.setProfileRow(profile);
            return;
        }
        const profileId = profile ?? LMEUserSettings.get()?.office_payroll_profile;
        if (profileId == null)
            Snackbar.showSnackbar("A default Office Payroll profile is not set. One can be set via User Settings.", { persist: true });
        else {
            const profileRow = await new ModelOfficePayrollProfile().searchSingle({ id: profileId }, "lme/backoffice/officepayroll/OfficePayrollProfileFilter");
            if (profileRow == null) {
                Snackbar.showSnackbar("The profile [" + profileId + "] was not found.", { persist: true });
            } else {
                await this.setProfileRow(profileRow);
            }
        }
    }

    public get profileRow(): RowOfficePayrollProfile {
        return this._profileRow;
    }

    public async setProfileRow(value: RowOfficePayrollProfile) {
        if (value != this._profileRow) {
            this._profileRow = value;
            await this.setPayAdjustmentCodes();
            if (!await this.checkBonusTaxRatesSetup()) {
                this.layoutGroupBy.tableGroupBy.selectedRow = null;
                this.layoutGroupBy.tableGroupBy.clearRows();
                return;
            }
            this.profileFilter = new OfficePayrollProcessingFilter(value);
            if (this._profileRow != null) {
                this.buttonTimecardProcessing.visible = this.layoutOfficePayrollProcessingPayeeDetail.panelTimecardDetails.visible = this.layoutOfficePayrollProcessingPayeeDetail.panelDivider.visible = this.profileRow.isNull("bonus_code");
                this.setCutoffDates();
                if (!this.cutoffDatesSet()) {
                    return;
                }

                this.setUpdateCutoffDateTooltip();
                this.sourceOrderPayablesCache.search({ profile_row: this.profileRow });
                this.sourcePayeeCache.search();
            }
        }
    }

    public setCutoffDates(): Date | null {
        this.snackbars.clear();
        this.payrollCutoffDate = this.getPayrollDateFromUrl("off_cutoff_date");
        const profileCutoffDate = DateUtil.parseDateWithKeywords(this.profileRow.get("cutoff_date"));
        this.recurringDate = this.getEarlierCutoffDate(this.payrollCutoffDate, profileCutoffDate);
        this.textboxPayrollCutoffDate.text = DateUtil.formatDate(DateUtil.justDate(this.payrollCutoffDate));
        this.textboxOverrideCutoffDate.text = DateUtil.formatDate(profileCutoffDate);
        this.setOverrideCutoffDateTooltip(profileCutoffDate, this.payrollCutoffDate);
        return profileCutoffDate;
    }

    private getPayrollDateFromUrl(field: keyof RowPayrollControlFields): Date | null {
        if (this.payrollControl) {
            return !this.payrollControl.isNull(field) ? new Date(this.payrollControl.get(field)) : null;
        } else {
            return getPayrollControlValue(field) ? new Date(getPayrollControlValue(field)) : null;
        }
    }

    private setUpdateCutoffDateTooltip() {
        const updateButton = new Button({
            caption: "Next Cutoff Date",
            color: "primary",
            onClick: async () => {
                const response = await this.updateCutoffDate();
                if (response) {
                    this.setCutoffDates();
                    this.profileFilter = new OfficePayrollProcessingFilter(this.profileRow, this.payrollCutoffDate);
                    this.sourceOrderPayablesCache.search({ profile_row: this.profileRow });
                    this.sourcePayeeCache.search({ is_office: true });
                }
            }
        });
        this.textboxPayrollCutoffDate.tooltipDynamicContent = updateButton;
    }

    private setOverrideCutoffDateTooltip(profileCutoffDate: Date, payrollCutoffDate: Date) {
        if (!profileCutoffDate) {
            this.textboxOverrideCutoffDate.tooltip = "Using the Payroll Cutoff Date to select " +
                "all records: settlements, pending pay adjustments, and recurring pay " +
                "adjustments.";
            return;
        }

        if (profileCutoffDate > payrollCutoffDate) {
            this.textboxOverrideCutoffDate.color = "error";
            this.textboxOverrideCutoffDate.tooltip = "Using the Override Cutoff Date to select " +
                "settlement and pending pay adjustment records. Using the Payroll Cutoff " +
                "Date (earlier date) to select recurring pay adjustments.";
        } else {
            this.textboxOverrideCutoffDate.color = "default";
            this.textboxOverrideCutoffDate.tooltip = "Using the Override Cutoff Date (earlier " +
                "date) to select all records: settlements, pending pay adjustments, and " +
                "recurring pay adjustments.";
        }

    }


    private cutoffDatesSet(): boolean {
        if (this.payrollCutoffDate == null) {
            const panel = this.createWarningSnackbar(`Office Cutoff Date must be entered on Settlement Control`, null, false);
            this.snackbars.add(panel);
            Snackbar.showSnackbar(panel, { persist: true });
            return false;
        }
        return true;
    }

    async setPayAdjustmentCodes() {
        if (!this.profileRow.isNull("bonus_code")) {
            const payAdjustmentCodes = [];
            const result: ModelSearchResult = await Model.search("lme/settlement/bonus-code-dtl", { bonus_code_hdr_id: this.profileRow.get("bonus_code") });
            result.modelRows?.forEach((row: ModelRow) => {
                if (row.isNull("deduct_code_id")) return;
                payAdjustmentCodes.push(row.get("deduct_code_id"));
            });
            (this.profileRow as ModelRow).set("pay_adjustment_codes", payAdjustmentCodes);
        }
    }

    async checkBonusTaxRatesSetup(): Promise<boolean> {
        if (this.checkingBonusTaxRates) return false;
        this.checkingBonusTaxRates = true;
        const bonusTaxMethod = this.profileRow.get("bonus_tax_method");
        this.snackbars.clear();
        let isValid = true;
        if (bonusTaxMethod === "B") {
            const fedTaxTable = await Model.searchSingleRecord("lme/settlement/fedtaxtable");
            if (fedTaxTable == null || fedTaxTable.isNull("bonus_tax_rate")) {
                const panel = this.createWarningSnackbar("The Bonus Tax Rate is not set in the Federal Tax Table. Please set it before processing office payroll.", () => {
                    return Navigation.navigateTo("lme/settlement/FederalTaxTable", { newTab: true });
                }, true);
                this.snackbars.add(panel);
                Snackbar.showSnackbar(panel, { persist: true });
                isValid = false;
            }
            const stateLocalTaxTables = await Model.search("lme/ar/statetax", { type_of: "S" });
            if (stateLocalTaxTables.modelRows.length === 0) {
                const panel = this.createWarningSnackbar("No State Tax Tables are set up. Please set them up before processing office payroll.", () => {
                    return Navigation.navigateTo("lme/settlement/StateTaxTableList", { newTab: true });
                }, true);
                this.snackbars.add(panel);
                Snackbar.showSnackbar(panel, { persist: true });
                isValid = false;
            } else {
                const stateLocalTaxTableIds = [];
                for (const taxTable of stateLocalTaxTables.modelRows) {
                    if (taxTable.isNull("bonus_tax_rate")) {
                        stateLocalTaxTableIds.push(taxTable.get("id"));
                    }
                }
                if (stateLocalTaxTableIds.length > 0) {
                    const panel = this.createWarningSnackbar(`The Bonus Tax Rate is not set for all State Tax Tables. Please set it before processing office payroll.`, () => {
                        return Navigation.navigateTo("lme/settlement/StateTaxTableList?ids=" + encodeURIComponent(stateLocalTaxTableIds.join(",")), { newTab: true });
                    }, true);
                    this.snackbars.add(panel);
                    Snackbar.showSnackbar(panel, { persist: true });
                    isValid = false;
                }
            }
        }
        this.checkingBonusTaxRates = false;
        return isValid;
    }

    private createWarningSnackbar(message: string, onClick: () => {}, includeButton: boolean): Panel {
        const panel = new Panel();
        const label = new Label({ text: message });
        const button = new Button({
            id: "buttonOpenRecord",
            caption: "Open Record",
            onClick: onClick
        });
        panel.add(label);
        if (includeButton) {
            panel.add(button);
        }
        return panel;
    }

    override get windowTitle(): string {
        const title = "Office Payroll Processing";
        const profileId = this.profileRow?.getString("id");
        return profileId ? `${title} - ${profileId}` : title;
    }

    sourceOrderPayablesCacheAfterExecution(event: DataSourceExecutionEvent) {
        if (event.getAction() == DataSourceAction.SEARCH) {
            this.payablesLookupMap = new PayablesLookup();
            this.profileFilter.payablesLookupMap = this.payablesLookupMap;
            this.payeeDataLookup = new PayeeDataLookup<OfficePayeeData>(() => new OfficePayeeData());
            this.layoutGroupBy.initialize(this.layoutOfficePayrollProcessingPayeeDetail, this.profileFilter, this.payeeDataLookup, this.profileRow, this.sourcePayeeCache, this.updateTimecardProcessingButton.bind(this), this.updateProcessButton.bind(this), this.syncTimecards.bind(this)).then(() => {
                this.createTimecardsAndUpdateRates();
            });
            this.setupMinuteTimer();
        }
    }

    sourceOrderPayablesCacheOnRemoteDataChange(event: DataSourceRemoteDataChangeEvent) {
        let changeType = event.change.type as ModelDataChangeType;
        const changeData = event.change.data as ModelRow;
        if (!this.profileFilter.dataPasses(changeData)) {
            changeType = ModelDataChangeType.DELETE;
        }
        const payeeData = this.payeeDataLookup.getPayeeData(changeData.get("payee_id"));
        let oldData: ModelRow;
        if (payeeData) {
            oldData = payeeData.findPayableById(changeData.get("record_id"));
        } else {
            oldData = this.sourceOrderPayablesCache.data.find(row => row.get("record_id") == changeData.get("record_id"));
        }
        if (this.sourceOrderPayablesCache.isSearching || (oldData != null && ObjectUtil.deepEqual(oldData.data, changeData.data))) {
            return;
        }
        this.sourceOrderPayablesCache.handleDataChange({
            type: changeType,
            data: changeData,
            lastUpdateNanos: event.change.lastUpdateNanos
        });
        this.layoutGroupBy.updateGroupByTableByPayable(oldData, changeData, changeType);
        event.preventDefault();
    }

    sourceVoidedPayablesOfficePayrollOnRemoteDataChange(event: DataSourceRemoteDataChangeEvent) {
        const changeData = event.change.data;
        if (changeData.isNull("table_name")) return;
        const row = this.sourceVoidedPayablesOfficePayroll.data.find(row => row.get("id") == changeData.get("id"));
        if (changeData.get("ready_to_pay_flag") == "V" && row == null) {
            this.sourceVoidedPayablesOfficePayroll.data.push(changeData);
        } else if (changeData.get("ready_to_pay_flag") != "V" && row != null) {
            this.sourceVoidedPayablesOfficePayroll.data = this.sourceVoidedPayablesOfficePayroll.data.filter(row => row.get("id") != changeData.get("id"));
        }
        this.updateArchiveVoidsButton();
        event.preventDefault();
    }

    sourceVoidedPayablesOfficePayrollAfterExecution(event: DataSourceExecutionEvent) {
        if (event.getAction() == DataSourceAction.SEARCH) {
            this.updateArchiveVoidsButton();
        }
    }

    sourcePayeeCacheOnRemoteDataChange(event: DataSourceRemoteDataChangeEvent) {
        let changeType = event.change.type as ModelDataChangeType;
        const changeData = event.change.data as ModelRow;
        if (changeData.isNull("payee_id")) return;
        const payeeId = changeData.get("payee_id");
        if (!this.profileFilter.dataPassesPayee(changeData)) {
            changeType = ModelDataChangeType.DELETE;
        }
        const oldData = this.sourcePayeeCache.data.find(row => row.get("payee_id") == payeeId);
        if (oldData != null && ObjectUtil.deepEqual(oldData.data, changeData.data)) {
            this.updatePayeeTotalsRefreshLabel();
            return;
        }
        this.sourcePayeeCache.handleDataChange({
            type: changeType as ModelDataChangeType,
            data: changeData,
            lastUpdateNanos: event.change.lastUpdateNanos
        });
        this.layoutGroupBy.updateGroupByTableByPayee(oldData, changeData, changeType as ModelDataChangeType).then(async () => {
            this.syncTimecards();
            this.updateTimecardProcessingButton();
            const payeeData = this.payeeDataLookup.getPayeeData(payeeId);
            if (payeeData) {
                payeeData.hasTaxUpdates = true;
                // if payee updated is currently selected payee, populate timecard details
                if (this.currentSelectedPayee && this.currentSelectedPayee.payeeId === payeeId) {
                    this.layoutOfficePayrollProcessingPayeeDetail.populateTimecardForPayee(payeeData);
                }
            }
        });
        event.preventDefault();
    }

    sourceStateFederalTaxDriverSettlementOnRemoteDataChange(event: DataSourceRemoteDataChangeEvent) {
        const changeData = event.change.data;
        if (changeData.isNull("payee_id")) {
            for (const payeeData of this.payeeDataLookup.getAllPayeeData()) {
                payeeData.hasTaxUpdates = true;
            }
        } else {
            // if payee updated is currently selected payee and panel is expanded, mark for refresh
            if (this.currentSelectedPayee && this.layoutPayeeTotals.expanded && this.currentSelectedPayee.payeeId === changeData.get("payee_id")) {
                this.payeeDataLookup.getPayeeData(changeData.get("payee_id")).hasTaxUpdates = true;
            }
        }
        // Update refresh label to show pending updates
        this.updatePayeeTotalsRefreshLabel();
        event.preventDefault();
    }

    private updateArchiveVoidsButton() {
        this.buttonArchiveVoids.caption = `Voids Ready To Archive (${this.sourceVoidedPayablesOfficePayroll.data.length})`;
        this.buttonArchiveVoids.enabled = this.sourceVoidedPayablesOfficePayroll.data.length > 0;
    }

    private updateTimecardProcessingButton() {
        const totalTimecards = this.sourceTimecard.data.length;
        const updatedTimecards = this.sourceTimecard.data.filter(row => !row.getCurrency("total_pay", new Currency(0)).isZero());
        this.buttonTimecardProcessing.caption = `Timecard Processing (${updatedTimecards.length}/${totalTimecards})`;
    }

    syncTimecards() {
        this.sourceTimecard.data = [];
        const timecardRows: ModelRow[] = [];
        for (const groupByRow of this.layoutGroupBy.tableGroupBy.rows) {
            const payeeId = groupByRow.data.get("payee_id");
            const payeeData = this.payeeDataLookup.getPayeeData(payeeId);
            if (!payeeData) {
                return;
            }
            const payeeTimecard = payeeData.getTimecard();
            if (payeeTimecard) {
                timecardRows.push(payeeTimecard);
            }
        }
        this.sourceTimecard.setRowsAndMode(DataSourceMode.UPDATE, timecardRows);
        this.sourceTimecard.data.forEach((row) => row._appending = false);
    }

    buttonArchiveVoidsOnClick(event: ClickEvent) {
        const cutoffDate = DateUtil.parseDateWithKeywords(this.profileRow.get("cutoff_date"));
        ArchiveVoidedPayables.showSlideout({ cutoff_date: cutoffDate, is_office: true });
    }

    async buttonTimecardProcessingOnClick(event: ClickEvent) {
        this.buttonTimecardProcessing.busy = true;
        await this.createTimecardsAndUpdateRates();
        TimecardProcessing.showCrud({ dataSource: this.sourceTimecard, payeeDataLookup: this.payeeDataLookup });
        this.buttonTimecardProcessing.busy = false;
    }

    private async createTimecardsAndUpdateRates() {
        for (const groupByRow of this.layoutGroupBy.tableGroupBy.rows) {
            const payeeId = groupByRow.data.get("payee_id");
            const payeeData = this.payeeDataLookup.getPayeeData(payeeId);
            const existingTimecard = this.sourceTimecard.data.find(row => row.get("id") === payeeId);
            if (existingTimecard) {
                // update rates if needed
                const payeeRow = payeeData.getPayeeRow();
                const payMethod = payeeRow.get("pay_method");
                const regRate = TimecardProcessing.calculateHourlyRateFromPayee(payeeRow, "regular_rate", payMethod);
                const otRate = TimecardProcessing.calculateHourlyRateFromPayee(payeeRow, "overtime_rate", payMethod);
                const vacRate = TimecardProcessing.calculateHourlyRateFromPayee(payeeRow, "vacation_rate", payMethod);
                const changed = regRate !== existingTimecard.get("regular_rate", 0) || otRate !== existingTimecard.get("overtime_rate", 0) || vacRate !== existingTimecard.get("vacation_rate", 0);
                if (changed) {
                    existingTimecard.set("regular_rate", regRate);
                    existingTimecard.set("overtime_rate", otRate);
                    existingTimecard.set("vacation_rate", vacRate);
                    TimecardProcessing.recalculateHoursAndTotals(existingTimecard);
                    await existingTimecard.post();
                    // also update the cached timecard rates to avoid the delay from live updates
                    payeeRow.set("tc_regular_rate", regRate);
                    payeeRow.set("tc_overtime_rate", otRate);
                    payeeRow.set("tc_vacation_rate", vacRate);
                }
            } else {
                const timecardRow = payeeData.addTimecard();
                if (timecardRow) {
                    timecardRow.set("payperiod_end", this.profileRow.getDate("cutoff_date", new Date()));
                    const payeeRow = payeeData.getPayeeRow();
                    const payMethod = payeeRow.get("pay_method");
                    const regRate = TimecardProcessing.calculateHourlyRateFromPayee(payeeRow, "regular_rate", payMethod);
                    const otRate = TimecardProcessing.calculateHourlyRateFromPayee(payeeRow, "overtime_rate", payMethod);
                    const vacRate = TimecardProcessing.calculateHourlyRateFromPayee(payeeRow, "vacation_rate", payMethod);
                    timecardRow.set("regular_rate", regRate);
                    timecardRow.set("overtime_rate", otRate);
                    timecardRow.set("vacation_rate", vacRate);
                    TimecardProcessing.recalculateHoursAndTotals(timecardRow);
                    try {
                        await timecardRow.post();
                        // cache for quick access
                        payeeRow.set("tc_regular_rate", regRate);
                        payeeRow.set("tc_overtime_rate", otRate);
                        payeeRow.set("tc_vacation_rate", vacRate);
                        this.sourceTimecard.data.push(timecardRow);
                    }
                    catch {
                        // ignore because another profile may have created the timecard already
                    }
                }
            }
        }
        this.sourceTimecard.data.forEach((row) => row._appending = false);
    }

    private navigateToRecurringPayAdjustment(payeeId: string, cutoffDate: Date, currencyType?: string) {
        const urlParams: string[] = [];
        urlParams.push(`payeeId=${payeeId}`);
        urlParams.push(`cutoffDate=${DateUtil.formatDate(cutoffDate, "yyyy-MM-dd")}`);
        if (currencyType) {
            urlParams.push(`currencyType=${currencyType}`);
        }
        Navigation.navigateTo("lme/settlement/officepayroll/OfficeRecurringPayAdjustmentList" + UrlUtil.buildQueryString(urlParams), { newTab: true });
    }

    public async updateSelectedPayee() {
        if (this.payeeDataLookup) {
            // Check if the currently selected payee has changes and the panel is expanded
            if (this.currentSelectedPayee && this.layoutPayeeTotals.expanded) {
                const selectedPayeeData = this.payeeDataLookup.getPayeeData(this.currentSelectedPayee.payeeId);
                if (selectedPayeeData && await selectedPayeeData.hasChanges()) {
                    // Update the selected payee's totals by calling the search method
                    const profileCutoffDate = DateUtil.parseDateWithKeywords(this.profileRow.get("cutoff_date"), true, false);
                    const cutoffDate = profileCutoffDate ?? this.payrollCutoffDate;
                    await this.layoutPayeeTotals.searchData(this.currentSelectedPayee, cutoffDate);

                    // Cache the API response
                    if (this.layoutPayeeTotals.activeRow) {
                        selectedPayeeData.setCachedApiResponse(this.layoutPayeeTotals.activeRow.createBasicCopy());
                        await selectedPayeeData.setTotals();
                        selectedPayeeData.hasTaxUpdates = false; // Reset tax updates after fetching
                    }
                }
            }
        }
    }

    private setTimer() {
        this.remainingSeconds = 30;
        this.timerId = window.setTimeout(() => this.executeTimerUpdates(), 30000); // 30 seconds
        this.startCountdown();
    }

    private setupMinuteTimer() {
        this.setTimer();
        this.addUnmountListener(() => this.clearTimer());
    }

    private executeTimerUpdates() {
        this.updateSelectedPayee().then(() => {
            this.setTimer();
        });
    }

    private clearTimer() {
        if (this.timerId !== null) {
            clearTimeout(this.timerId);
            this.timerId = null;
        }
        if (this.countdownTimerId !== null) {
            clearInterval(this.countdownTimerId);
            this.countdownTimerId = null;
        }
    }

    private startCountdown() {
        if (this.countdownTimerId !== null) {
            clearInterval(this.countdownTimerId);
        }
        this.countdownTimerId = window.setInterval(() => {
            this.remainingSeconds--;
            this.updatePayeeTotalsRefreshLabel();
            if (this.remainingSeconds <= 0) {
                clearInterval(this.countdownTimerId);
                this.countdownTimerId = null;
            }
        }, 1000);
        this.updatePayeeTotalsRefreshLabel();
    }

    private async updatePayeeTotalsRefreshLabel() {
        if (this.currentSelectedPayee && this.layoutPayeeTotals.expanded) {
            const selectedPayeeData = this.payeeDataLookup.getPayeeData(this.currentSelectedPayee.payeeId);
            if (selectedPayeeData && selectedPayeeData.getTotalsLastChangedDateTime() != null) {
                if (await selectedPayeeData.hasChanges(false)) {
                    this.layoutPayeeTotals.labelRefresh.visible = true;
                    this.layoutPayeeTotals.labelRefresh.text = `Refresh (${this.remainingSeconds}s)`;
                } else {
                    this.layoutPayeeTotals.labelRefresh.visible = false;
                }
            } else {
                this.layoutPayeeTotals.labelRefresh.visible = false;
            }
        } else {
            this.layoutPayeeTotals.labelRefresh.visible = false;
        }
    }

    setupPayeeTotalsLayout() {
        // Set up the updateRefreshLabel callback
        this.layoutPayeeTotals.updateRefreshLabel = () => this.updatePayeeTotalsRefreshLabel();
        this.layoutGroupBy.onPayeeSelected = async (props: OfficePayeeSelectedProps) => {
            this.currentSelectedPayee = props;
            this.layoutPayeeTotals.visible = true;
            this.layoutPayeeTotals.refresh = async (forceUpdate: boolean = false) => {
                this.layoutPayeeTotals.labelPayeeId.text = this.layoutPayeeTotals.labelPayeeId.tooltip = props.payeeName;
                if (this.currentSelectedPayee && this.layoutPayeeTotals.expanded) {
                    this.layoutPayeeTotals.labelPayeeId.busy = true;
                    const selectedPayeeData = this.payeeDataLookup.getPayeeData(this.currentSelectedPayee.payeeId);
                    const timecard = props.payeeData.getTimecard();
                    this.layoutPayeeTotals.imageNeedsReview.visible = false;
                    this.layoutPayeeTotals.panelTimecard.visible = false;
                    this.layoutPayeeTotals.labelViewTimecard.visible = false;
                    if (selectedPayeeData) {
                        // Check if we have cached data and totals haven't changed
                        if (selectedPayeeData.hasCachedApiResponse() && await selectedPayeeData.hasChanges() === false && !forceUpdate) {
                            // Use cached data instead of making API call
                            if (this.layoutPayeeTotals.activeRow) {
                                this.layoutPayeeTotals.activeRow.clear();
                                this.layoutPayeeTotals.activeRow.setValues(selectedPayeeData.getCachedApiResponse().data);
                                this.layoutPayeeTotals.hidePanelsFieldsWithNoData();
                            }
                        } else {
                            // Make API call to get fresh data
                            const profileCutoffDate = DateUtil.parseDateWithKeywords(this.profileRow.get("cutoff_date"), true, false);
                            const cutoffDate = profileCutoffDate ?? this.payrollCutoffDate;
                            await this.layoutPayeeTotals.searchData(props, cutoffDate);

                            // Cache the API response
                            if (this.layoutPayeeTotals.activeRow) {
                                selectedPayeeData.setCachedApiResponse(this.layoutPayeeTotals.activeRow.createBasicCopy());
                                await selectedPayeeData.setTotals();
                                selectedPayeeData.hasTaxUpdates = false; // Reset tax updates after fetching
                            }
                        }
                    }
                }
                this.layoutPayeeTotals.labelPayeeId.busy = false;
                // Update refresh label after refreshing data
                this.updatePayeeTotalsRefreshLabel();
            };
            this.layoutPayeeTotals.viewTimecardOnClick = async () => {
                await this.syncTimecards();
                TimecardProcessing.showCrud({
                    dataSource: this.sourceTimecard,
                    payeeId: props.payeeName,
                    payeeDataLookup: this.payeeDataLookup
                });
            };
            this.layoutPayeeTotals.onRecurringsClick = () => {
                const cutoffDate = this.getEarlierCutoffDate(DateUtil.parseDateWithKeywords(this.profileRow.get("cutoff_date")), this.payrollCutoffDate);
                const currency = CurrencySettings.getSingleton().multiCurrencyEnabled() ? "USD" : null;
                this.navigateToRecurringPayAdjustment(props.payeeId, cutoffDate, currency);
            };
            if (this.layoutPayeeTotals.expanded) {
                this.layoutPayeeTotals.refresh();
            }
            // Update refresh label visibility and text
            this.updatePayeeTotalsRefreshLabel();

        };
        this.layoutGroupBy.onPayeeDeselected = () => {
            this.currentSelectedPayee = null;
            this.layoutPayeeTotals.visible = false;
            this.layoutPayeeTotals.expanded = false;
            this.layoutPayeeTotals.labelRefresh.visible = false;
        };
    }

    private getEarlierCutoffDate(date1: Date, date2: Date): Date {
        return date1 && date2
            ? (date1 <= date2 ? date1 : date2)
            : date1 || date2;
    }

    private updateProcessButton(): void {
        const hasAcceptedPayee = this.layoutGroupBy.acceptedPayeeIds.size > 0;

        this.buttonProcess.enabled = hasAcceptedPayee;
        this.buttonProcess.tooltip = hasAcceptedPayee ? "Process Office" : "There are no Accepted Payees";
    }

    buttonManageRecurringOnClick() {
        const layout = Layout.getLayout("lme/settlement/ManageRecurringPayAdjustments") as ManageRecurringPayAdjustments;
        SlideoutDecorator.decorateAndSlideIn(layout, {
            width: "100%",
            fillVerticalSpace: true,
            title: "Manage Recurring Pay Adjustments",
            transitionOptions: {
                direction: Alignment.BOTTOM
            },
            layoutLoadListeners: () => {
                layout.mainDataSource.orderBy = [{ field: "payee_id", sort: "asc" }];
                layout.mainDataSource.url = "lme/backoffice/officepayroll/manage-recurring-pay-adjustments";
                layout.props = this.getManageRecurringProps();
                layout.buttonSearch.visible = false;
                layout.textboxDate.text = DateUtil.formatDate(layout.props.date);
                layout.textboxDate.enabled = false;
                layout.buttonSearch.clicked();
            }
        });
    }

    private getManageRecurringProps(): ManageRecurringPayAdjustmentProps {
        const overrideCutoffDate = DateUtil.parseDateWithKeywords(this.profileRow.get("cutoff_date"));

        // Use payroll cutoff if override cutoff is null, otherwise use the earlier date
        let cutoffDate: Date;
        if (overrideCutoffDate == null) {
            cutoffDate = this.payrollCutoffDate ?? new Date();
        } else {
            cutoffDate = this.payrollCutoffDate && overrideCutoffDate > this.payrollCutoffDate
                ? this.payrollCutoffDate
                : overrideCutoffDate;
        }

        return {
            date: cutoffDate,
            currency_code: "USD",
            payee_status: this.profileRow.get("payee_status"),
            payees: this.profileRow.get("payee_ids"),
            payees_exclude: this.profileRow.getBoolean("payee_ids_exclude", false),
            frozen_payables: this.profileRow.get("frozen_payables"),
            payment_method: this.profileRow.get("payment_method"),
            bonus_code: this.profileRow.get("bonus_code"),
            bonus_tax_method: this.profileRow.get("bonus_tax_method"),
            override_frequency: this.profileRow.get("override_frequency"),
            is_office: true,
            showDisabledTooltip: false
        }
    }

    private async checkRecurringPayAdjustments(): Promise<boolean> {
        const response = await Api.search("lme/backoffice/officepayroll/manage-recurring-pay-adjustments", this.getManageRecurringProps());
        if (response?.data?.length > 0) {
            const isYes = await CommonDialogs.showYesNo("There are recurring pay adjustments that have not been processed for this pay period. Would you like to process them now?", "Manage Recurring Pay Adjustments");
            if (isYes) {
                this.buttonManageRecurringOnClick();
                return Promise.resolve(false);
            } else if (isYes === undefined) {
                return Promise.resolve(false);
            }
        }
        return Promise.resolve(true);
    }

    getReadyToPayAdjustments(): ModelRow[] {
        const filter = new OfficePayrollProcessingFilter(this.profileRow);
        const records: ModelRow[] = [];
        for (const row of this.sourceOrderPayablesCache.data) {
            if (CurrencySettings.getSingleton().multiCurrencyEnabled() && row.getCurrency("pay_amount").currency_code !== "USD") {
                continue; // Skip rows with different currency
            }
            if (filter.dataPasses(row) && !row.isNull("payee_process_status") && row.get("ready_to_pay_flag") == "Y") {
                records.push(row);
            }
        }
        return records;
    }

    async buttonProcessOnClick(event: ClickEvent) {
        this.buttonProcess.busy = true;
        const shouldContinue = await this.checkRecurringPayAdjustments();
        if (!shouldContinue) {
            this.buttonProcess.busy = false;
            return;
        }

        const payeeIdSet = new Set<string>();
        const checkNumbersSet = new Set<string>();
        const readyToPayPayables = this.getReadyToPayAdjustments();

        const acceptedPayeeRows = this.layoutGroupBy.tableGroupBy.rows.filter((row: TableRow) => !row.data.isNull("process_status"));
        if (acceptedPayeeRows.length === 0) {
            CommonDialogs.showError("No payees have been accepted for payment. Please review and try again.", "No Payees Accepted");
            this.buttonProcess.busy = false;
            return;
        }
        acceptedPayeeRows.forEach(row => {
            payeeIdSet.add(row.data.get("payee_id"));
        });

        const payeeIdArray = Array.from(payeeIdSet);
        const checkNumbersArray = Array.from(checkNumbersSet);
        const totalPay = new Currency(acceptedPayeeRows.reduce((sum, row) => {
            return sum + row.data.getCurrency("process_total", new Currency(0)).getAmount();
        }, 0));

        const deductions = readyToPayPayables.filter(row => row.get("record_type") === "O").map(row => row.get("record_id"));

        const processCount = readyToPayPayables.length;

        // Determine cutoff date: use the earlier of profile and payroll dates, or payroll date if profile is not set
        const profileCutoffDate = DateUtil.parseDateWithKeywords(this.profileRow.get("cutoff_date"), true, false);
        const cutoffDate = profileCutoffDate ?? this.payrollCutoffDate;

        OrderPayablesProcessStepper.showAsSlideout({
            title: `Process Office (${payeeIdArray.length})`,
            deductionIds: deductions.join(","),
            checkNumbers: checkNumbersArray.join(","),
            checkNumberCount: checkNumbersArray.length,
            cutoffDate: cutoffDate,
            recurringDate: this.recurringDate,
            endDate: cutoffDate,
            payeeIds: payeeIdArray.join(","),
            profileRow: this.profileRow,
            processCount,
            selectedPayables: readyToPayPayables,
            total: totalPay,
            isDriverSettlement: false,
            isOffice: true,
            displayRecordSelectionDates: true,
            payrollCutoffDate: this.payrollCutoffDate,
        }).finally(() => {
            this.buttonProcess.busy = false;
        });
    }
}

